import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import ShareIcon from '@mui/icons-material/Share';
import HelpIcon from '@mui/icons-material/Help';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import { clearGraphQLErrorMessage } from 'common/util/string';
import ELEMENT from 'common/element';
import { QueryType } from 'common/graphqlTypes';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { useShortUrl } from 'hooks/useShortUrl';

import Button from 'components/common/Button/Button';
import Toolbar from 'components/common/Toolbar/Toolbar';
import GraphStyleDropdown from 'components/common/Graphs/GraphStyleDropdown/GraphStyleDropdown';
import SearchActionsDropdown, { ActionMenuItemConfig, SearchActions } from 'components/search/SearchActionsDropdown';
import CompareDropdown from 'components/search/SearchGraphsPage/CompareDropdown';
import { TriggerType } from 'components/alerts/SaveAlertDialog';
import { handleExport } from 'components/common/Graphs/ScalyrGraph';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { absoluteResolvedUrlState, isCrossTeamSearchState, searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import { plotDataRequestsState } from 'components/search/SearchLogsPage/SearchStateManager.state';
import { buildUrl } from 'common/util/location';
import { isSafari } from 'service/browser';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { getRequestId } from 'common/util/lrqRequest';
import { getTimeDifferenceInMs, parseDuration, timezoneMoment } from 'common/util/time';
import { UrlParams } from 'common/types';

import { currentChartRefState } from 'components/common/Graphs/graphs.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import BreakdownSelect from 'components/search/SearchGraphsPage/BreakdownSelect';
import PostGraphToSlackDialog from './PostGraphToSlackDialog';

import 'components/common/Button/Button.scss';
import './GraphResultsToolbar.scss';

// button icons
const fastRewindIcon = <FastRewindIcon fontSize="small" />;
const fastForwardIcon = <FastForwardIcon fontSize="small" />;
const shareIcon = <ShareIcon fontSize="small" />;
const helpIcon = <HelpIcon fontSize="small" />;


/**
 * GraphResultsToolbar is a toolbar with buttons that allow the user to interact with a graph and the search that created it.
 */
function GraphResultsToolbar(): JSX.Element {

  const setSearchParams = useSetSearchParams();
  const absoluteUrl = useRecoilValue(absoluteResolvedUrlState);
  const searchParams = useRecoilValue(searchParamRecordState);
  const { startTime, endTime, postGraphToSlackOpen } = searchParams;
  const [postGraphToSlackDialogOpened, setPostGraphToSlackDialogOpened] = useState(postGraphToSlackOpen === 'true');
  const plotDataRequests = useRecoilValue(plotDataRequestsState);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const [shortUrl, setShortUrl] = useState<string | null>(null);
  const timezone = useRecoilValue(timezoneState);
  const wrappedMoment = timezoneMoment(timezone);
  const isCrossTeamSearch = useRecoilValue(isCrossTeamSearchState);
  const copy = useCopyToClipboard();

  const facetDataId = getRequestId(plotDataRequests, QueryType.TOP_FACETS);
  const { results: [ { facetData }] } = useEventCache([ facetDataId ]);
  const facetList = facetData.facets?.map((facet) => facet.name).sort() || [];
  const [ previousShortenedUrl,  setPreviousShortenedUrl ] = useState<string>('');
  const [copyLinkIsAvailable, setCopyLinkIsAvailable] = useState(!isSafari);
  const currentChartRef = useRecoilValue(currentChartRefState); // The ref to the current chart instance
  const { fetchShortUrlPromise } = useShortUrl();
  useEffect(() => {
    if (postGraphToSlackOpen === 'true') {
      const params = new URLSearchParams(window.location.search);
      params.delete(K.URL_PARAM.POST_GRAPH_TO_SLACK_OPEN);
      window.history.replaceState(null, '', '?' + params + window.location.hash);
    }
  }, [postGraphToSlackOpen]);

  // Grab the current chart instance, and use it to export the graph as PNG
  const downloadGraphAsPNG = (): void => {
    const chart = currentChartRef?.current?.chart;
    if (chart) {
      let title = K.DEFAULT_EXPORT_GRAPH_TITLE;
      const subtitle = !searchParams.facet && searchParams.breakdownFacet ? ` broken down by ${searchParams.breakdownFacet}` : '';
      title += subtitle;
      if (searchParams.facet) {
        title = `Graph of ${searchParams.facet}`;
      }

      handleExport(chart, title);
    }
  };

  const copyLinkToGraph = () => {
    if (isSafari) {
      if (!copyLinkIsAvailable) {
        return;
      }
      if (shortUrl) {
        copy(shortUrl, 'link');
      } else {
        setPageAlertConfig({ message: clearGraphQLErrorMessage('Could not copy link to clipboard'), variant: PageAlertVariant.ERROR, autoHide: true });
      }
    } else if (previousShortenedUrl !== absoluteUrl) {
      const currentGraphLink = buildUrl(absoluteUrl);
      fetchShortUrlPromise(currentGraphLink).then(({ data }) => {
        copy(data?.compressUrl || '', 'link');
      });
    }
  };

  const dropdownClickTrigger = (): void => {
    if (isSafari) {
      if (previousShortenedUrl !== absoluteUrl) {
        setCopyLinkIsAvailable(false);
        const currentGraphLink = buildUrl(absoluteUrl);
        fetchShortUrlPromise(currentGraphLink).then(({ data }) => {
          setShortUrl(data?.compressUrl || '');
          setPreviousShortenedUrl(absoluteUrl);
          setCopyLinkIsAvailable(true);
        });
      }
    }
  };

  const dashAndAlertsSection: Array<string | ActionMenuItemConfig> = [];
  if (!isCrossTeamSearch) {
    dashAndAlertsSection.push(SearchActions.ALERT);
  }
  dashAndAlertsSection.push(SearchActions.DASHBOARD);
  const saveMenuSections: Array<Array<string | ActionMenuItemConfig>> = [
    [{ action: SearchActions.SAVE, text: 'Save Graph' }],
    dashAndAlertsSection,
    [{ action: SearchActions.DOWNLOAD, text: 'Download as PNG', clickHandler: downloadGraphAsPNG }],
  ];

  const shareMenuSections = [
    [
      {
        text:         copyLinkIsAvailable ? 'Copy Link' : 'Preparing Link To Share',
        clickHandler: copyLinkToGraph,
        disabled:     !copyLinkIsAvailable,
        testId:       ELEMENT.searchGraphCopyLink,
      },
    ],
    [
      {
        text:         'Post Graph to Slack',
        testId:       ELEMENT.searchGraphPostToSlack,
        clickHandler: (): void => {
          setPostGraphToSlackDialogOpened(true);
        },
      },
    ],
    [SearchActions.SAVE_SHARED],
  ];

  const saveAlertDialogConfig = {
    triggerType: TriggerType.AVERAGE,
  };

  const currentTime = wrappedMoment();

  const onOlderButtonClick = (): void => {
    let newStartTime;
    let newEndTime;

    if (startTime && endTime) {
      const duration = getTimeDifferenceInMs({ endTime, startTime }, null);
      newEndTime = wrappedMoment(Number(endTime)).subtract(duration / 2, 'milliseconds');
      newStartTime = wrappedMoment(Number(startTime)).subtract(duration / 2, 'milliseconds');
    } else {
      const startTimeDuration = startTime || '4 hours';
      const duration = parseDuration(startTimeDuration);
      newEndTime = wrappedMoment(currentTime).subtract(duration / 2, 'milliseconds');
      newStartTime = wrappedMoment(currentTime).subtract(duration + (duration / 2), 'milliseconds');
    }

    setSearchParams({
      [K.URL_PARAM.START_TIME]: newStartTime.format('x'),
      [K.URL_PARAM.END_TIME]: newEndTime.format('x'),
    } as UrlParams);
  };

  const onNewerButtonClick = (): void => {
    let newEndTime;
    let newStartTime;
    let duration;

    if (startTime && endTime) {
      duration = getTimeDifferenceInMs({ endTime, startTime }, null);
      newEndTime = wrappedMoment(Number(endTime)).add(duration / 2, 'milliseconds');
      newStartTime = wrappedMoment(Number(startTime)).add(duration / 2, 'milliseconds');
    } else {
      const startTimeDuration = startTime || '4 hours';
      duration = parseDuration(startTimeDuration);
      newEndTime = wrappedMoment();
      newStartTime = wrappedMoment().subtract(duration, 'milliseconds');
    }

    if (wrappedMoment(newEndTime).isAfter(wrappedMoment())) {
      newEndTime = wrappedMoment();
      newStartTime = wrappedMoment().subtract(duration, 'milliseconds');
    }

    setSearchParams({
      [K.URL_PARAM.START_TIME]: newStartTime.format('x'),
      [K.URL_PARAM.END_TIME]: newEndTime.format('x'),
    } as UrlParams);
  };

  const group1 = (
    <ButtonGroup>
      <Button onClick={onOlderButtonClick} size="sm" leftIcon={fastRewindIcon}>
        Older
      </Button>
      <Button onClick={onNewerButtonClick} size="sm" rightIcon={fastForwardIcon}>
        Newer
      </Button>
    </ButtonGroup>
  );

  const group2 = (
    <ButtonGroup>
      <SearchActionsDropdown
        text="Save"
        sections={saveMenuSections}
        saveAlertDialogConfig={saveAlertDialogConfig}
        testId={ELEMENT.searchGraph}
        buttonTestId={ELEMENT.searchGraphSaveButton}
      />
      <SearchActionsDropdown
        text="Share"
        sections={shareMenuSections}
        leftIcon={shareIcon}
        shouldFetchShortUrlAfterOpen
        dropdownClickTrigger={dropdownClickTrigger}
        testId={ELEMENT.searchGraph}
        buttonTestId={ELEMENT.searchGraphShareButton}
      />
    </ButtonGroup>
  );

  const group3 = (
    <>
      { !searchParams.breakdownFacet ? <CompareDropdown /> : null }
    </>
  );

  const group4 = (
    <>
      { !searchParams.compareTimeOffset && facetList.length > 0 ? <BreakdownSelect facetList={facetList} /> : null }
    </>
  );

  const leftItems = [group1, group2, group3, group4 ];

  const rightItems = [
    <GraphStyleDropdown buttonSize="sm"/>,
    <Button
      size="sm"
      leftIcon={helpIcon}
      href={HELP_ROUTE.HELP_GRAPHS}
      openInNewTab
      testId={ELEMENT.searchGraphHelp}
    >
      Help
    </Button>,
  ];


  return (
    <div className="GraphResultsToolbar">
      <Toolbar leftItems={leftItems} rightItems={rightItems} />
      <PostGraphToSlackDialog
        dialogOpened={postGraphToSlackDialogOpened}
        setDialogOpened={setPostGraphToSlackDialogOpened}
      />
    </div>
  );
}

export default GraphResultsToolbar;
